import {
  Box,
  Flex,
  useToast,
  Text,
  Heading,
  Switch,
  Modal,
  ModalOverlay,
  ModalContent,
  useDisclosure,
  Button,
  ModalBody,
  Spinner,
  SimpleGrid,
  GridItem,
  Image,
  Center,
  Badge,
  Select,
  Alert,
  AlertDescription,
  AlertIcon,
  Stack,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import {
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { BiArrowBack } from "react-icons/bi";
import avatar from "../../../assets/images/avatar.png";
import { changeWorkerStatusApi } from "../../../api/workers";
import { useMutation, useQuery } from "react-query";
import {
  getAdminWorkersByUnitApi,
  getRoleCatalogApi,
  updateWorkerRoleApi,
} from "../../../api/admin";
import { ROLE_OPTIONS, ROLES } from "../../../auth/roles";
import { PERMISSION_LABELS } from "../../../auth/permissions";

const roleLabel = (role) =>
  ROLE_OPTIONS.find(([value]) => value === role)?.[1] || role;

const Workers = () => {
  const toast = useToast();
  const [worker, setWorker] = useState(null);
  const [postData, setPostData] = useState({});
  const [selectedRole, setSelectedRole] = useState("staff");
  const actionModal = useDisclosure();

  const getAdminWorkersByUnitQuery = useQuery(
    ["getAdminWorkersByUnitApi"],
    () => getAdminWorkersByUnitApi(),
    { refetchInterval: 30 * 1000 },
  );
  const workers = getAdminWorkersByUnitQuery?.data?.data?.workers;
  const roleCatalogQuery = useQuery(["getRoleCatalogApi"], getRoleCatalogApi);
  const roleCatalog = roleCatalogQuery.data?.data?.roles || [];
  const selectedRoleDetails = roleCatalog.find(
    (item) => item.role === selectedRole,
  );

  useEffect(() => {
    if (!worker || !workers) return;
    const refreshedWorker = workers.find((item) => item._id === worker._id);
    if (refreshedWorker) setWorker(refreshedWorker);
  }, [worker, workers]);

  useEffect(() => {
    if (worker) setSelectedRole(worker.role || "staff");
  }, [worker?._id, worker?.role]);

  const changeWorkerStatusMutation = useMutation(
    (form) => changeWorkerStatusApi(form.status, form.workerId),
    {
      onSuccess: async (res) => {
        await getAdminWorkersByUnitQuery.refetch();
        actionModal.onClose();
      },
      onError: (err) => {
        toast({
          title: "Update error",
          description:
            err?.response?.data?.msg ||
            "An error occurred while updating worker",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      },
    },
  );

  const updateRoleMutation = useMutation(updateWorkerRoleApi, {
    onSuccess: async (response) => {
      await getAdminWorkersByUnitQuery.refetch();
      setWorker(response.data.worker);
      toast({
        title: "Worker role updated",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error) =>
      toast({
        title: "Unable to update role",
        description: error.response?.data?.msg || "Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      }),
  });

  const changeWorkerStatus = ({ status, workerId }) => {
    changeWorkerStatusMutation.mutate({ status, workerId });
  };

  const openModal = (checked, workerId) => {
    actionModal.onOpen();
    setPostData({ status: checked ? "active" : "inactive", workerId });
  };

  const handleActivate = () => {
    changeWorkerStatus(postData);
  };

  if (getAdminWorkersByUnitQuery.isLoading) {
    return (
      <Center minH="320px" w="full" px="24px" flexDirection="column">
        <Spinner
          size="lg"
          thickness="3px"
          speed="0.7s"
          color="brand.500"
          emptyColor="gray.200"
        />
        <Heading mt="4" size="sm" color="gray.700">
          Loading workers
        </Heading>
        <Text mt="1" color="gray.500" fontSize="sm" textAlign="center">
          Fetching staff accounts and their current availability.
        </Text>
      </Center>
    );
  }

  return (
    <Box px="24px" w="full">
      {worker ? (
        <Box px="24px" w="full">
          <Flex justify={"space-between"} align={"center"} mt="10px" mb="20px">
            <BiArrowBack cursor={"pointer"} onClick={() => setWorker(null)} />
            <Heading
              size="sm"
              textAlign={"center"}
              textDecoration={"underline"}
            >
              Staff Details
            </Heading>
            <Box w="25px" />
          </Flex>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing="10px">
            <GridItem colSpan={{ base: 1, md: 3 }}>
              <Center w="full" py="20px">
                <Image
                  h="140px"
                  w="140px"
                  borderRadius={"full"}
                  src={worker?.image}
                  fallbackSrc={avatar}
                />
              </Center>
            </GridItem>
            <Text noOfLines={1}>
              <Text as="span" fontWeight={500}>
                Presence:{" "}
              </Text>
              <Badge
                colorScheme={worker?.presence?.isOnline ? "green" : "gray"}
              >
                {worker?.presence?.isOnline ? "Online" : "Offline"}
              </Badge>
            </Text>
            <Text noOfLines={1}>
              <Text as="span" fontWeight={500}>
                Last seen:{" "}
              </Text>
              <Text as="span">
                {worker?.presence?.lastSeenAt
                  ? new Date(worker.presence.lastSeenAt).toLocaleString()
                  : "Not available"}
              </Text>
            </Text>
            <Text noOfLines={1}>
              <Text as="span" fontWeight={500}>
                First Name:{" "}
              </Text>
              <Text as="span">{worker?.firstName}</Text>
            </Text>
            <Text noOfLines={1}>
              <Text as="span" fontWeight={500}>
                Last Name:{" "}
              </Text>
              <Text as="span">{worker?.lastName}</Text>
            </Text>
            <Text noOfLines={1}>
              <Text as="span" fontWeight={500}>
                Phone Number:{" "}
              </Text>
              <Text as="span">{worker?.phoneNumber}</Text>
            </Text>
            <Text noOfLines={1}>
              <Text as="span" fontWeight={500}>
                Sub unit:{" "}
              </Text>
              <Text as="span">{worker?.subUnit}</Text>
            </Text>
            <Text noOfLines={1}>
              <Text as="span" fontWeight={500}>
                Email:{" "}
              </Text>
              <Text as="span">{worker?.email}</Text>
            </Text>
            <Text noOfLines={1}>
              <Text as="span" fontWeight={500}>
                Unit:{" "}
              </Text>
              <Text as="span">{worker?.unit}</Text>
            </Text>
            <Text noOfLines={1}>
              <Text as="span" fontWeight={500}>
                Staff Id:{" "}
              </Text>
              <Text as="span">{worker?.idNo}</Text>
            </Text>

            <GridItem colSpan={{ base: 1, md: 3 }} mt="5">
              <Flex
                borderWidth="1px"
                borderRadius="xl"
                p={{ base: "4", md: "5" }}
                mb="4"
                justify="space-between"
                align="center"
                gap="4"
              >
                <Box>
                  <Heading size="sm">Account access</Heading>
                  <Text mt="1" color="gray.500" fontSize="sm">
                    {worker.status === "active"
                      ? "This worker can currently sign in to the system."
                      : "This worker cannot sign in until the account is activated."}
                  </Text>
                </Box>
                <Flex align="center" gap="3">
                  <Badge
                    colorScheme={
                      worker.status === "active" ? "green" : "orange"
                    }
                  >
                    {worker.status}
                  </Badge>
                  <Switch
                    size="lg"
                    colorScheme="green"
                    aria-label={`${worker.status === "active" ? "Deactivate" : "Activate"} ${worker.firstName} ${worker.lastName}`}
                    isChecked={worker.status === "active"}
                    isDisabled={changeWorkerStatusMutation.isLoading}
                    onChange={(event) =>
                      openModal(event.target.checked, worker._id)
                    }
                  />
                </Flex>
              </Flex>
              <Box
                borderWidth="1px"
                borderRadius="xl"
                p={{ base: "4", md: "5" }}
              >
                <Flex
                  justify="space-between"
                  gap="3"
                  align={{ base: "flex-start", md: "center" }}
                  direction={{ base: "column", md: "row" }}
                >
                  <Box>
                    <Heading size="sm">Role and permissions</Heading>
                    <Text mt="1" color="gray.500" fontSize="sm">
                      Review the capabilities before assigning this worker’s
                      role.
                    </Text>
                  </Box>
                  <Badge
                    colorScheme={
                      worker.role === ROLES.SUPER_ADMIN
                        ? "red"
                        : worker.role === ROLES.UNIT_ADMIN
                          ? "purple"
                          : "blue"
                    }
                  >
                    {roleLabel(worker.role || "staff")}
                  </Badge>
                </Flex>
                <Flex
                  mt="4"
                  gap="3"
                  align={{ base: "stretch", md: "flex-end" }}
                  direction={{ base: "column", md: "row" }}
                >
                  <Box flex="1">
                    <Text fontWeight="700" mb="1">
                      Access role
                    </Text>
                    <Select
                      value={selectedRole}
                      isDisabled={
                        updateRoleMutation.isLoading ||
                        roleCatalogQuery.isLoading
                      }
                      onChange={(event) => setSelectedRole(event.target.value)}
                    >
                      {(roleCatalog.length
                        ? roleCatalog
                        : ROLE_OPTIONS.map(([role]) => ({
                            role,
                            assignable: role !== ROLES.SUPER_ADMIN,
                          }))
                      )
                        .filter(
                          (item) =>
                            item.assignable || item.role === worker.role,
                        )
                        .map((item) => (
                          <option
                            key={item.role}
                            value={item.role}
                            disabled={!item.assignable}
                          >
                            {roleLabel(item.role)}
                            {!item.assignable ? " (cannot assign)" : ""}
                          </option>
                        ))}
                    </Select>
                  </Box>
                  <Button
                    minW="130px"
                    isLoading={updateRoleMutation.isLoading}
                    isDisabled={
                      selectedRole === (worker.role || "staff") ||
                      selectedRoleDetails?.assignable === false
                    }
                    colorScheme={
                      selectedRole === ROLES.SUPER_ADMIN ? "red" : "blue"
                    }
                    onClick={() =>
                      updateRoleMutation.mutate({
                        workerId: worker._id,
                        role: selectedRole,
                      })
                    }
                  >
                    Assign role
                  </Button>
                </Flex>

                {selectedRole === ROLES.SUPER_ADMIN && (
                  <Alert
                    mt="4"
                    status="error"
                    borderRadius="lg"
                    alignItems="flex-start"
                  >
                    <AlertIcon mt="1px" />
                    <AlertDescription>
                      <Text fontWeight="800">
                        Super Administrator — unrestricted system access
                      </Text>
                      <Text mt="1" fontSize="sm">
                        This role can perform every current and future action,
                        including managing workers and assigning roles. Assign
                        it only to a trusted system owner.
                      </Text>
                    </AlertDescription>
                  </Alert>
                )}
                {selectedRole === ROLES.UNIT_ADMIN && (
                  <Alert
                    mt="4"
                    status="warning"
                    borderRadius="lg"
                    alignItems="flex-start"
                  >
                    <AlertIcon mt="1px" />
                    <AlertDescription>
                      <Text fontWeight="800">
                        Unit Administrator — scoped administrative access
                      </Text>
                      <Text mt="1" fontSize="sm">
                        This worker can administer workers and activity
                        associated with {worker.unit}. Only a Super
                        Administrator can assign or remove this role.
                      </Text>
                    </AlertDescription>
                  </Alert>
                )}

                <Box mt="4" bg="gray.50" borderRadius="lg" p="4">
                  <Text fontWeight="800" fontSize="sm">
                    {roleLabel(selectedRole)} can:
                  </Text>
                  {roleCatalogQuery.isLoading ? (
                    <Spinner mt="3" size="sm" />
                  ) : (
                    <Stack mt="3" spacing="2">
                      {(selectedRoleDetails?.permissions || []).map(
                        (permission) => (
                          <Flex key={permission} gap="2" align="flex-start">
                            <Text color="green.500" fontWeight="900">
                              ✓
                            </Text>
                            <Box>
                              <Text fontSize="sm" fontWeight="600">
                                {PERMISSION_LABELS[permission] || permission}
                              </Text>
                              <Text color="gray.500" fontSize="xs">
                                {permission}
                              </Text>
                            </Box>
                          </Flex>
                        ),
                      )}
                    </Stack>
                  )}
                </Box>
              </Box>
            </GridItem>
          </SimpleGrid>
        </Box>
      ) : (
        <Box w="full" overflowX={"scroll"}>
          <TableContainer>
            <Table>
              <Thead>
                <Tr>
                  <Th size="sm">Name</Th>
                  <Th size="sm">Account access</Th>
                  <Th size="sm">Email</Th>
                  <Th size="sm">Last login</Th>
                  <Th size="sm">Presence</Th>
                  <Th size="sm">Status</Th>
                  <Th size="sm">Role</Th>
                  <Th size="sm">Unit</Th>
                  <Th size="sm">Sub-unit</Th>
                </Tr>
              </Thead>
              <Tbody>
                {workers?.map((worker) => (
                  <Tr
                    cursor={"pointer"}
                    key={worker._id}
                    w="full"
                    justify={"space-between"}
                    align={"center"}
                    py="2"
                  >
                    <Td
                      onClick={() => setWorker(worker)}
                    >{`${worker.firstName} ${worker.lastName}`}</Td>
                    <Td>
                      <Flex align="center" gap="2" minW="120px">
                        <Switch
                          colorScheme="green"
                          aria-label={`${worker.status === "active" ? "Deactivate" : "Activate"} ${worker.firstName} ${worker.lastName}`}
                          onChange={(event) =>
                            openModal(event.target.checked, worker._id)
                          }
                          isChecked={worker.status === "active"}
                          isDisabled={changeWorkerStatusMutation.isLoading}
                        />
                        <Text fontSize="sm" fontWeight="600">
                          {worker.status === "active" ? "Active" : "Inactive"}
                        </Text>
                      </Flex>
                    </Td>
                    <Td onClick={() => setWorker(worker)}>{worker.email}</Td>
                    <Td onClick={() => setWorker(worker)}>
                      {worker?.lastLogin
                        ? new Date(worker?.lastLogin).toLocaleString()
                        : "-"}
                    </Td>
                    <Td onClick={() => setWorker(worker)}>
                      <Badge
                        colorScheme={
                          worker?.presence?.isOnline ? "green" : "gray"
                        }
                      >
                        {worker?.presence?.isOnline ? "Online" : "Offline"}
                      </Badge>
                    </Td>
                    <Td onClick={() => setWorker(worker)}>{worker.status}</Td>
                    <Td onClick={() => setWorker(worker)}>
                      {worker.role || "Legacy"}
                    </Td>
                    <Td onClick={() => setWorker(worker)}>{worker.unit}</Td>
                    <Td onClick={() => setWorker(worker)}>{worker.subUnit}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      )}
      <Modal
        isCentered
        isOpen={actionModal?.isOpen}
        onClose={actionModal?.onClose}
      >
        <ModalOverlay />
        <ModalContent maxW="400px" minHeight={"200px"}>
          <ModalBody>
            {changeWorkerStatusMutation.isLoading ? (
              <Spinner size="xl" />
            ) : (
              <Box my="10%">
                <Heading size="md" textAlign="center">
                  Are you sure you want to{" "}
                  {postData?.status === "active" ? "activate" : "deactivate"}{" "}
                  this worker?
                </Heading>
                <Flex justify={"space-between"} align="center" mt="30px">
                  <Button w="45%" onClick={handleActivate}>
                    Yes
                  </Button>
                  <Button w="45%" onClick={actionModal?.onClose}>
                    No
                  </Button>
                </Flex>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Workers;
